import React, { useEffect } from 'react';
import { StrandLoader } from './BrandMark';
import { SAYINGS, type SayingScene } from '../utils/sayings';

/**
 * Sent on the window when a full-screen loading screen goes away: what was behind it (a
 * workspace that has just opened) is in view from then on. The canvas shows its hint about
 * pan and zoom for a few seconds from there.
 */
export const LOADING_SCREEN_CLOSED = 'noodle:loading-screen-closed';

/**
 * The crystal ball: a clear sphere with the palette drifting inside it (styles: .ns-orb in
 * index.css). No glow around it; its colours alone make it stand out. The loading mark of the
 * loading screens and inside a node while it generates. Decorative; pair it with text that
 * says what is happening.
 */
export const GlassOrb: React.FC<{ size?: number; className?: string }> = ({ size = 148, className = '' }) => (
    <div className={`ns-orb ${className}`} style={{ '--orb-size': `${size}px` } as React.CSSProperties} aria-hidden="true" data-glass-orb>
        <div className="ns-orb__body">
            <div className="ns-orb__swirl">
                <div className="ns-orb__blob ns-orb__blob--lavender" />
                <div className="ns-orb__blob ns-orb__blob--blush" />
                <div className="ns-orb__blob ns-orb__blob--deep" />
                <div className="ns-orb__blob ns-orb__blob--peach" />
                <div className="ns-orb__blob ns-orb__blob--mint" />
            </div>
            <div className="ns-orb__grain" />
            <div className="ns-orb__shine" />
        </div>
    </div>
);

interface LoadingCardProps {
    /** Which wait this is (utils/sayings.ts): its picture and its line */
    scene?: SayingScene;
    /** What is happening, in plain words; the step under way */
    title: string;
    detail?: string;
    className?: string;
}

/**
 * The card every wait shows (docs/DESIGN_SYSTEM.md, "Loading"): a glass panel, one picture of
 * Pip and friends, the scene's saying, the strand drawing itself, and the line that says what
 * is happening. The words are keyed, so a new step fades in.
 */
export const LoadingCard: React.FC<LoadingCardProps> = ({ scene = 'open', title, detail, className = '' }) => {
    const saying = SAYINGS[scene];
    return (
        <div className={`ns-loading-card ns-glass ns-glass-strong ${className}`} data-loading-scene={scene}>
            <img src={saying.picture} alt={saying.alt} className="ns-loading-card__picture" decoding="async" draggable={false} />
            <p className="ns-loading-card__line">{saying.line}</p>
            <div className="ns-loading-card__status">
                <StrandLoader width={44} />
                <div className="min-w-0">
                    <div key={title} className="ns-loading-text ns-t-title truncate">{title}</div>
                    {detail && <div key={detail} className="ns-loading-text ns-t-small truncate">{detail}</div>}
                </div>
            </div>
        </div>
    );
};

/**
 * A full-screen loading screen: the orb, the scene's saying, and what is happening, over
 * everything. No picture: the orb is the loading mark, the words are the brand's.
 */
export const LoadingScreen: React.FC<LoadingCardProps> = ({ scene = 'start', title, detail, className = 'fixed inset-0 z-[200]' }) => {
    useEffect(() => () => { window.dispatchEvent(new Event(LOADING_SCREEN_CLOSED)); }, []);
    const saying = SAYINGS[scene];
    return (
        <div className={`${className} bg-cream flex flex-col items-center justify-center p-6 text-center`} role="status" aria-live="polite" data-loading-screen data-loading-scene={scene}>
            <GlassOrb />
            {/* The words stand clear of the orb and its glow (the line's own class sets its margin to 0, so the room is the wrapper's) */}
            <div className="mt-16 flex flex-col items-center">
                <p className="ns-loading-card__line">{saying.line}</p>
                {/* Keyed by the words, so a new step fades in */}
                <div key={title} className="ns-loading-text mt-3 ns-t-title text-ink-soft">{title}</div>
                {detail && <div key={detail} className="ns-loading-text mt-1 ns-t-small">{detail}</div>}
            </div>
        </div>
    );
};

export default GlassOrb;
